import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Switch,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import {
  FaUndo,
  FaCheck,
  FaEdit,
  FaPause,
} from "react-icons/fa";
import { LuWorkflow } from "react-icons/lu";
import lodash from "lodash";
import cronstrue from "cronstrue";
import { MdEdit, MdOutlineIntegrationInstructions, MdSchedule } from "react-icons/md";
import { ScheduledJobProto, ScheduledScenarioJobProto, ScheduledJobType } from "build-ts/schedule";
import { SlackChannel, SlackNotificationConfig, SlackNotificationRequest } from "build-ts/slack_notification";
import { Header } from "build-ts/metadata";
import { MetadataTypeEnum } from "build-ts/enums";
import { v4 as uuidv4 } from "uuid";
// import { getSlackChannels } from "services/AnalystService";
import { putScheduledJob } from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import WorkflowProcessSectionHeading from "components/analyst/WorkflowProcessSectionHeading";
import { MarkdownRenderer } from "components/agentChat/MessageComponent";
import ArtifactPanelHeader from "./ArtifactPanelHeader";
import { PiFlowArrowBold } from "react-icons/pi";

type TabId = "analysis" | "instructions" | "schedule";

interface ScenarioArtifactCardProps {
  /** SCHEDULED_JOB artifact (from reasoning message) */
  scheduleJob?: ScheduledJobProto | null;
  /** Analyst id for saving the schedule */
  analystId?: string;
  /** Whether this job is already saved */
  isSavedInAnalyst: boolean;
  /** Whether this is the most-recently selected card */
  isSelected?: boolean;
  /** Called when user removes the artifact card */
  onRemove: () => void;
  /** Called after the job is successfully saved, with the persisted job */
  onSave?: (savedJob: ScheduledJobProto) => void;
  /** Start expanded */
  defaultExpanded?: boolean;
  /** When this value changes, collapse the card */
  collapseSignal?: number;
  /** When this value changes, expand the card */
  expandSignal?: number;
  /** Whether the artifact panel is currently expanded to full width */
  isExpanded?: boolean;
  /** Called when the expand/collapse toggle button is clicked */
  onExpandToggle?: () => void;
}

const ScenarioArtifactCard: React.FC<ScenarioArtifactCardProps> = ({
  scheduleJob: initialScheduleJob,
  analystId,
  isSavedInAnalyst,
  isSelected = false,
  onRemove,
  onSave,
  defaultExpanded = true,
  collapseSignal,
  expandSignal,
  isExpanded,
  onExpandToggle,
}) => {

  const [activeTab, setActiveTab] = useState<TabId>("analysis");
  const [isReportingEditMode, setIsReportingEditMode] = useState(false);
  const [isGuidanceEditMode, setIsGuidanceEditMode] = useState(false);

  // Form state — initialised from scheduleJob
  const [jobName, setJobName] = useState(initialScheduleJob?.header?.name ?? "");
  const [trigger, setTrigger] = useState(initialScheduleJob?.scheduled_analysis?.trigger ?? "");
  const [reportingInstructions, setReportingInstructions] = useState(
    initialScheduleJob?.scheduled_analysis?.reporting_instructions ?? ""
  );
  const [analysisInstructions, setAnalysisInstructions] = useState(
    initialScheduleJob?.scheduled_analysis?.analysis_instructions ?? ""
  );
  const [historicalInstructions, setHistoricalInstructions] = useState(
    initialScheduleJob?.scheduled_analysis?.historical_instructions ?? ""
  );
  const [temporaryInstructions, setTemporaryInstructions] = useState(
    initialScheduleJob?.scheduled_analysis?.temporary_instructions ?? ""
  );

  // Schedule tab state
  const [cronExpression, setCronExpression] = useState(initialScheduleJob?.cron_expr ?? "");
  const [isScheduleActive, setIsScheduleActive] = useState(initialScheduleJob?.is_active ?? false);
  const [slackTitle, setSlackTitle] = useState(
    initialScheduleJob?.scheduled_analysis?.slack_notification_request?.notification_config?.message_title ?? ""
  );
  // const [slackChannels, setSlackChannels] = useState<SlackChannel[]>([]);
  const [selectedSlackChannel, setSelectedSlackChannel] = useState<SlackChannel | null>(() => {
    const ch = initialScheduleJob?.scheduled_analysis?.slack_notification_request?.notification_config?.channel;
    return ch?.id || ch?.name ? (ch as SlackChannel) : null;
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showCronHumanReadable, setShowCronHumanReadable] = useState(true);

  const initialSlackChannel = (() => {
    const ch = initialScheduleJob?.scheduled_analysis?.slack_notification_request?.notification_config?.channel;
    return ch?.id || ch?.name ? (ch as SlackChannel) : null;
  })();

  const isChanged =
    !lodash.isEqual(jobName, initialScheduleJob?.header?.name ?? "") ||
    !lodash.isEqual(trigger, initialScheduleJob?.scheduled_analysis?.trigger ?? "") ||
    !lodash.isEqual(reportingInstructions, initialScheduleJob?.scheduled_analysis?.reporting_instructions ?? "") ||
    !lodash.isEqual(analysisInstructions, initialScheduleJob?.scheduled_analysis?.analysis_instructions ?? "") ||
    !lodash.isEqual(historicalInstructions, initialScheduleJob?.scheduled_analysis?.historical_instructions ?? "") ||
    !lodash.isEqual(temporaryInstructions, initialScheduleJob?.scheduled_analysis?.temporary_instructions ?? "") ||
    !lodash.isEqual(cronExpression, initialScheduleJob?.cron_expr ?? "") ||
    !lodash.isEqual(isScheduleActive, initialScheduleJob?.is_active ?? false) ||
    !lodash.isEqual(slackTitle, initialScheduleJob?.scheduled_analysis?.slack_notification_request?.notification_config?.message_title ?? "") ||
    !lodash.isEqual(selectedSlackChannel, initialSlackChannel);

  // Re-sync all fields when scheduleJob prop changes
  useEffect(() => {
    if (initialScheduleJob) {
      setJobName(initialScheduleJob.header?.name ?? "");
      setTrigger(initialScheduleJob.scheduled_analysis?.trigger ?? "");
      setReportingInstructions(initialScheduleJob.scheduled_analysis?.reporting_instructions ?? "");
      setAnalysisInstructions(initialScheduleJob.scheduled_analysis?.analysis_instructions ?? "");
      setHistoricalInstructions(initialScheduleJob.scheduled_analysis?.historical_instructions ?? "");
      setTemporaryInstructions(initialScheduleJob.scheduled_analysis?.temporary_instructions ?? "");
      setCronExpression(initialScheduleJob.cron_expr ?? "");
      setIsScheduleActive(initialScheduleJob.is_active ?? false);
      setSlackTitle(
        initialScheduleJob.scheduled_analysis?.slack_notification_request?.notification_config?.message_title ?? ""
      );
      const channel = initialScheduleJob.scheduled_analysis?.slack_notification_request?.notification_config?.channel;
      setSelectedSlackChannel(channel?.id || channel?.name ? (channel as SlackChannel) : null);
      setIsReportingEditMode(false);
      setIsGuidanceEditMode(false);
    } else {
      setJobName("");
      setTrigger("");
      setReportingInstructions("");
      setAnalysisInstructions("");
      setHistoricalInstructions("");
      setTemporaryInstructions("");
      setCronExpression("");
      setIsScheduleActive(false);
      setSlackTitle("");
      setSelectedSlackChannel(null);
      setIsReportingEditMode(false);
      setIsGuidanceEditMode(false);
    }
  }, [initialScheduleJob]);

  // Fetch Slack channels
  // useEffect(() => {
  //   const fetchChannels = async () => {
  //     const channels = await getSlackChannels();
  //     if (channels) setSlackChannels(channels);
  //   };
  //   fetchChannels();
  // }, []);

  const handleSave = async () => {
    if (!analystId) {
      showToast("Analyst context is required to save.", "", "warning");
      return;
    }
    if (jobName.trim() === "") {
      showToast("Please enter a job name", "", "warning");
      return;
    }
    setIsSaving(true);
    try {
      const job = ScheduledJobProto.create({
        header: Header.create({
          id: initialScheduleJob?.header?.id ?? uuidv4(),
          name: jobName.trim(),
          type: MetadataTypeEnum.SCHEDULED_JOB,
          owner_id: analystId,
        }),
        type: ScheduledJobType.SCENARIO_JOB,
        cron_expr: cronExpression,
        is_active: isScheduleActive,
        scheduled_analysis: ScheduledScenarioJobProto.create({
          trigger: trigger.trim(),
          darwin_name: initialScheduleJob?.scheduled_analysis?.darwin_name,
          analysis_instructions: analysisInstructions.trim(),
          reporting_instructions: reportingInstructions.trim(),
          historical_instructions: historicalInstructions.trim(),
          temporary_instructions: temporaryInstructions.trim(),
          slack_notification_request: SlackNotificationRequest.create({
            notification_config: SlackNotificationConfig.create({
              channel: selectedSlackChannel
                ? SlackChannel.create({
                  id: selectedSlackChannel.id,
                  name: selectedSlackChannel.name,
                  is_private: selectedSlackChannel.is_private,
                  num_members: selectedSlackChannel.num_members,
                })
                : undefined,
              message_title: slackTitle.trim(),
            }),
          }),
        }),
      });
      const response = await putScheduledJob(job);
      if (response) {
        showToast("Schedule saved successfully", "", "success");
        onSave?.(response);
      } else {
        showToast("Failed to save schedule", "", "error");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setJobName(initialScheduleJob?.header?.name ?? "");
    setTrigger(initialScheduleJob?.scheduled_analysis?.trigger ?? "");
    setReportingInstructions(initialScheduleJob?.scheduled_analysis?.reporting_instructions ?? "");
    setAnalysisInstructions(initialScheduleJob?.scheduled_analysis?.analysis_instructions ?? "");
    setHistoricalInstructions(initialScheduleJob?.scheduled_analysis?.historical_instructions ?? "");
    setTemporaryInstructions(initialScheduleJob?.scheduled_analysis?.temporary_instructions ?? "");
    setIsReportingEditMode(false);
    setIsGuidanceEditMode(false);
  };

  const cronHumanReadable = (() => {
    if (!cronExpression.trim()) return null;
    try {
      return cronstrue.toString(cronExpression.trim());
    } catch {
      return null;
    }
  })();

  const displayLabel = jobName?.trim() || initialScheduleJob?.header?.name?.trim() || "New Job";

  const handleFieldChange = <T extends string>(
    setter: React.Dispatch<React.SetStateAction<T>>
  ) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setter(e.target.value as T);
  };

  const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
    { id: "analysis", label: "Definition", icon: PiFlowArrowBold },
    { id: "schedule", label: "Schedule", icon: MdSchedule },
    { id: "instructions", label: "Guidance", icon: MdOutlineIntegrationInstructions },
  ];

  // SAVED/DRAFT status badge — shown in the header actions area
  const savedDraftBadge = isSavedInAnalyst ? (
    <Text
      fontSize="2xs"
      color="green.800"
      bg="green.100"
      px={2}
      py={0.5}
      borderRadius="sm"
      fontWeight="700"
      flexShrink={0}
      mb={0}
      border="1px solid"
      borderColor="green.200"
    >
      SAVED
    </Text>
  ) : (
    <Text
      fontSize="2xs"
      color="orange.700"
      bg="yellow.100"
      px={2}
      py={0.5}
      borderRadius="sm"
      fontWeight="700"
      flexShrink={0}
      mb={0}
      border="1px solid"
      borderColor="yellow.500"
    >
      DRAFT
    </Text>
  );

  return (
    <Box
      width="100%"
      h="100%"
      minH={0}
      bg="white"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <ArtifactPanelHeader
        icon={LuWorkflow}
        iconBg="white"
        iconColor="purple.700"
        name={displayLabel}
        typeLabel="Workflow"
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as TabId)}
        actions={savedDraftBadge}
        isExpanded={isExpanded}
        onExpandToggle={onExpandToggle}
      />

      {/* Tab content: on Analysis, outer strip does not scroll; Instructions fills height and scrolls inside. */}
      <Box
        flex={1}
        minH={0}
        display="flex"
        flexDirection="column"
        overflowY={activeTab === "analysis" ? "hidden" : "auto"}
        sx={
          activeTab === "analysis"
            ? undefined
            : {
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: "var(--chakra-colors-gray-300)",
                borderRadius: "2px",
              },
            }
        }
      >
        {activeTab === "analysis" && (
          <Flex
            direction="column"
            flex="1"
            minH={0}
            minW={0}
            w="100%"
            px={2}
            py={2}
            gap={2.5}
          >
            <FormControl display="flex" flexDirection="column" minW={0} flexShrink={0}>
              <WorkflowProcessSectionHeading label="Name" compact />
              <Input
                value={jobName}
                onChange={handleFieldChange(setJobName)}
                placeholder="e.g. Weekly CPL Monitor…"
                size="sm"
                fontSize="0.8rem"
                fontWeight="500"
                w="100%"
                _focus={{
                  borderColor: "purple.500",
                  boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                }}
              />
            </FormControl>
            <FormControl display="flex" flexDirection="column" minW={0} flexShrink={0}>
              <WorkflowProcessSectionHeading
                label="Objective"
                compact
                tooltip="When should this analysis fire? Define the metric thresholds and data conditions."
              />
              <Textarea
                value={trigger}
                onChange={handleFieldChange(setTrigger)}
                placeholder="e.g. CPL_today > 100 AND CPL_3day_avg < 50, spend > 0…"
                rows={4}
                resize="none"
                minH="3.25rem"
                w="100%"
                _focus={{
                  borderColor: "purple.500",
                  boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                }}
                fontWeight="500"
                fontSize="0.8rem"
              />
            </FormControl>
            <FormControl
              display="flex"
              flexDirection="column"
              flex="1"
              minH={0}
              minW={0}
              w="100%"
            >
              <Box flexShrink={0}>
                <WorkflowProcessSectionHeading
                  label="Instructions"
                  compact
                  tooltip="Same rendering as chat messages (markdown, GFM, math). Use Edit for raw source."
                  rightSlot={
                    isReportingEditMode ? (
                      <Button
                        size="xs"
                        colorScheme="purple"
                        variant="solid"
                        leftIcon={<Icon as={FaCheck} boxSize={3.5} />}
                        onClick={() => setIsReportingEditMode(false)}
                        fontSize="xs"
                      >
                        Done
                      </Button>
                    ) : (
                      <Button
                        size="2xs"
                        variant="ghost"
                        colorScheme="purple"
                        leftIcon={<Icon as={MdEdit} boxSize={4} />}
                        onClick={() => setIsReportingEditMode(true)}
                        fontSize="xs"
                      >
                        Edit
                      </Button>
                    )
                  }
                />
              </Box>
              {isReportingEditMode ? (
                <Box flex="1" minH={0} w="100%" display="flex" flexDir="column">
                  <Textarea
                    value={reportingInstructions}
                    onChange={handleFieldChange(setReportingInstructions)}
                    placeholder="Write markdown: headings, lists, tables (GFM), code fences…"
                    flex="1"
                    minH={0}
                    w="100%"
                    resize="none"
                    overflowY="auto"
                    fontWeight="500"
                    fontSize="sm"
                    fontFamily="mono"
                    _focus={{
                      borderColor: "purple.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                    }}
                  />
                </Box>
              ) : (
                <Box position="relative" flex="1" minH={0} w="100%">
                  <Tooltip label="Locked. Click Edit to modify." placement="left" hasArrow>
                    <Box
                      position="absolute"
                      inset={0}
                      w="100%"
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      px={4}
                      py={0}
                      bg="white"
                      overflowY="auto"
                      overflowX="auto"
                      cursor="not-allowed"
                      sx={{
                        "&::-webkit-scrollbar": { width: "4px" },
                        "&::-webkit-scrollbar-track": { background: "transparent" },
                        "&::-webkit-scrollbar-thumb": {
                          background: "var(--chakra-colors-gray-300)",
                          borderRadius: "2px",
                        },
                      }}
                    >
                      {reportingInstructions.trim() ? (
                        <MarkdownRenderer
                          content={reportingInstructions}
                          isArtifactsPanelVisible={true}
                        />
                      ) : (
                        <Text fontSize="sm" color="gray.500" mb={0}>
                          No instructions yet. Click Edit to add markdown.
                        </Text>
                      )}
                    </Box>
                  </Tooltip>
                </Box>
              )}
            </FormControl>
          </Flex>
        )}

        {/* Job “guidance” view: For Internal Use Only — same Edit / markdown as workflow */}
        {activeTab === "instructions" && (
          <Flex
            direction="column"
            flex="1"
            minH={0}
            w="100%"
            overflow="hidden"
            px={2}
            py={2}
          >
            <FormControl
              display="flex"
              flexDirection="column"
              flex="1"
              minH={0}
              w="100%"
            >
              <WorkflowProcessSectionHeading
                label="For Internal Use Only"
                compact
                tooltip="Internal guidance for the agent to perform workflow tasks more reliably."
                rightSlot={
                  isGuidanceEditMode ? (
                    <Button
                      size="sm"
                      colorScheme="purple"
                      variant="solid"
                      leftIcon={<Icon as={FaCheck} boxSize={3.5} />}
                      onClick={() => setIsGuidanceEditMode(false)}
                    >
                      Done
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="purple"
                      leftIcon={<Icon as={MdEdit} boxSize={4} />}
                      onClick={() => setIsGuidanceEditMode(true)}
                    >
                      Edit
                    </Button>
                  )
                }
              />
              {isGuidanceEditMode ? (
                <Textarea
                  flex="1"
                  minH={{ base: "200px", lg: "220px" }}
                  value={analysisInstructions}
                  onChange={handleFieldChange(setAnalysisInstructions)}
                  placeholder={"# analysis instructions\n# how to build the data query…"}
                  w="100%"
                  minW={0}
                  resize="none"
                  overflowY="auto"
                  fontWeight="500"
                  fontSize="sm"
                  fontFamily="mono"
                  _focus={{
                    borderColor: "purple.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                  }}
                />
              ) : (
                <Tooltip label="Locked. Click Edit to modify." placement="top" hasArrow>
                  <Box
                    flex="1"
                    minH={0}
                    w="100%"
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    px={4}
                    py={3}
                    bg="gray.50"
                    overflowY="auto"
                    overflowX="auto"
                    cursor="not-allowed"
                  >
                    {analysisInstructions.trim() ? (
                      <MarkdownRenderer
                        content={analysisInstructions}
                        isArtifactsPanelVisible={true}
                      />
                    ) : (
                      <Text fontSize="sm" color="gray.500" mb={0}>
                        No guidance yet. Click Edit to add markdown or plain text.
                      </Text>
                    )}
                  </Box>
                </Tooltip>
              )}
            </FormControl>
          </Flex>
        )}

        {activeTab === "schedule" && (
          <Box px={2} py={2}>
            <VStack spacing={5} align="stretch">
              <FormControl>
                <Flex gap={2} align="flex-end" w="100%">
                  <Box flex="3" minW={0}>
                    <FormLabel fontSize="sm" mb={1} fontWeight="600">
                      <WorkflowProcessSectionHeading
                        label={showCronHumanReadable ? "Run frequency" : "Cron expression"}
                        compact
                      />
                    </FormLabel>
                    <Tooltip
                      label={
                        showCronHumanReadable
                          ? cronExpression || "No cron expression set"
                          : cronHumanReadable || "Invalid or unparsable cron"
                      }
                      placement="top"
                      hasArrow
                      isDisabled={!cronExpression.trim() || !cronHumanReadable}
                    >
                      <InputGroup size="sm">
                        <Input
                          value={
                            showCronHumanReadable && cronHumanReadable
                              ? cronHumanReadable
                              : cronExpression
                          }
                          onChange={(e) => {
                            if (!showCronHumanReadable || !cronHumanReadable) {
                              setCronExpression(e.target.value);
                            }
                          }}
                          placeholder="e.g. 0 0 5 ? * MON"
                          fontFamily={
                            showCronHumanReadable && cronHumanReadable ? "body" : "mono"
                          }
                          fontSize="sm"
                          h="2.25rem"
                          pr={cronExpression.trim() && cronHumanReadable ? "1.75rem" : undefined}
                          isReadOnly={showCronHumanReadable && !!cronHumanReadable}
                          _focus={{
                            borderColor: "purple.500",
                            boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                          }}
                        />
                        {cronExpression.trim() && cronHumanReadable && (
                          <InputRightElement width="1.75rem" height="100%" pointerEvents="auto">
                            <Box
                              as="button"
                              type="button"
                              display="inline-flex"
                              alignItems="center"
                              justifyContent="center"
                              w="1.25rem"
                              h="1.25rem"
                              position="relative"
                              borderRadius="sm"
                              onClick={() => {
                                if (!cronHumanReadable) return;
                                setShowCronHumanReadable((prev) => !prev);
                              }}
                              _hover={{ bg: "gray.100" }}
                            >
                              <Icon
                                as={showCronHumanReadable && cronHumanReadable ? FaEdit : FaCheck}
                                boxSize={3}
                                color="gray.500"
                              />
                            </Box>
                          </InputRightElement>
                        )}
                      </InputGroup>
                    </Tooltip>
                  </Box>
                  <Box flex="1" minW={0}>
                    <FormLabel
                      fontSize="sm"
                      fontWeight="600"
                      mb={1}
                      color="transparent"
                      userSelect="none"
                    >
                      &nbsp;
                    </FormLabel>
                    <HStack spacing={2} align="center" h="2.25rem">
                      <Switch
                        size="sm"
                        isChecked={isScheduleActive}
                        onChange={(e) => setIsScheduleActive(e.target.checked)}
                        colorScheme="green"
                        mb={0}
                      />
                      <HStack
                        spacing={1}
                        align="center"
                        bg={isScheduleActive ? "green.50" : "gray.50"}
                        borderRadius="md"
                        px={2}
                        py={1}
                      >
                        {isScheduleActive ? (
                          <Box
                            w="7px"
                            h="7px"
                            borderRadius="full"
                            bg="green.400"
                            flexShrink={0}
                          />
                        ) : (
                          <Icon as={FaPause} boxSize={2} color="gray.500" />
                        )}
                        <Text
                          fontSize="xs"
                          fontWeight="700"
                          color={isScheduleActive ? "green.700" : "gray.500"}
                          mb={0}
                        >
                          {isScheduleActive ? "LIVE" : "Paused"}
                        </Text>
                      </HStack>
                    </HStack>
                  </Box>
                </Flex>
              </FormControl>

              <FormControl>
                <WorkflowProcessSectionHeading label="Temporary instructions" compact />
                <Textarea
                  value={temporaryInstructions}
                  onChange={(e) => setTemporaryInstructions(e.target.value)}
                  placeholder="One-off instructions that override defaults for a single run…"
                  fontSize="sm"
                  rows={4}
                  resize="none"
                  _focus={{
                    borderColor: "purple.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                  }}
                />
              </FormControl>
            </VStack>
          </Box>
        )}
      </Box>

      {/* ── Footer Actions ── */}
      <HStack px={2} pt={1} pb={2} bg="white" justify="end" flexShrink={0}>
        {isChanged && (
          <HStack spacing={2}>
            <Button
              size="xs"
              colorScheme="red"
              variant="outline"
              leftIcon={<Icon as={FaUndo} boxSize={3} />}
              onClick={(e) => {
                e.stopPropagation();
                handleDiscard();
              }}
              isDisabled={isSaving}
              fontWeight="500"
            >
              Discard Changes
            </Button>
            <Button
              size="xs"
              colorScheme="green"
              leftIcon={<Icon as={FaCheck} boxSize={3} />}
              onClick={async (e) => {
                e.stopPropagation();
                await handleSave();
              }}
              isLoading={isSaving}
              fontWeight="500"
            >
              <Text fontSize="xs" fontWeight="700" mb={0}>
                {isSavedInAnalyst ? "Update" : "Save"}
              </Text>
            </Button>
          </HStack>
        )}
      </HStack>
    </Box>
  );
};

export default ScenarioArtifactCard;
